import React, { useContext, useState } from "react";
import { Box, Button, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { UserContextType, useAuth } from "../auth";
import { groupService } from "../service/GroupService";
import { GroupSummary } from "../types/group";

const Emitter = require("tiny-emitter/instance");

interface GroupMembershipActionProps {
  group: GroupSummary;
  // Called after a successful join/cancel so the caller can refetch.
  onChanged: () => void;
  // Shows a "View group" button for members (used on the list page).
  showViewButton?: boolean;
}

// The join / pending / member control shared by the groups list and the group page.
const GroupMembershipAction: React.FC<GroupMembershipActionProps> = ({ group, onChanged, showViewButton }) => {
  const navigate = useNavigate();
  const { IsAuthenticated, getToken } = useContext<UserContextType>(useAuth);
  const [busy, setBusy] = useState(false);

  // The cards are clickable, so keep button clicks from also opening the group.
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  const handleJoin = async (e: React.MouseEvent) => {
    stop(e);
    if (!IsAuthenticated || !IsAuthenticated() || !getToken) {
      navigate("/login");
      return;
    }
    setBusy(true);
    try {
      await groupService.join(group.id, getToken());
      Emitter.emit("notify", `Your request to join ${group.name} has been sent.`, "success");
      onChanged();
    } catch (err) {
      Emitter.emit("notify", "Failed to send your request. Please try again.", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async (e: React.MouseEvent) => {
    stop(e);
    if (!getToken) return;
    setBusy(true);
    try {
      await groupService.leave(group.id, getToken());
      Emitter.emit("notify", "Your request has been cancelled.", "success");
      onChanged();
    } catch (err) {
      Emitter.emit("notify", "Failed to cancel your request.", "error");
    } finally {
      setBusy(false);
    }
  };

  if (group.membershipStatus === "Pending") {
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
        <Chip label="Request pending" color="warning" variant="outlined" size="small" />
        <Button size="small" onClick={handleCancel} disabled={busy}>
          Cancel request
        </Button>
      </Box>
    );
  }

  if (group.membershipStatus === "Approved") {
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
        <Chip label="Member" color="success" size="small" />
        {showViewButton && (
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => {
              stop(e);
              navigate(`/groups/${group.id}`);
            }}
          >
            View group
          </Button>
        )}
      </Box>
    );
  }

  // None or Declined
  return (
    <Button variant="contained" size="small" onClick={handleJoin} disabled={busy}>
      Request to Join
    </Button>
  );
};

export default GroupMembershipAction;
